import {
    GoogleAuthProvider,
    OAuthProvider,
    getAdditionalUserInfo,
    signInWithPopup,
    updateProfile,
    type AdditionalUserInfo,
    type User,
    type UserCredential,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { provisionOwnerUnlessInvited } from '@/lib/provisionNewOwnerUser';
import type { FederatedProvider } from '@/lib/federatedProviders';

export type GooglePopupOptions = {
    loginHint?: string;
};

function resolveOAuthDisplayName(user: User, additionalUserInfo?: AdditionalUserInfo | null): string {
    const fromUser = user.displayName?.trim();
    if (fromUser) return fromUser;

    const profile = additionalUserInfo?.profile;
    if (profile && typeof profile === 'object') {
        const record = profile as Record<string, unknown>;
        const name = record.name;
        if (name && typeof name === 'object') {
            const nameRecord = name as Record<string, unknown>;
            const first = String(nameRecord.firstName ?? '').trim();
            const last = String(nameRecord.lastName ?? '').trim();
            const full = [first, last].filter(Boolean).join(' ');
            if (full) return full;
        }
        const given = String(record.given_name ?? '').trim();
        const family = String(record.family_name ?? '').trim();
        const fullFromGiven = [given, family].filter(Boolean).join(' ');
        if (fullFromGiven) return fullFromGiven;
    }

    return user.email?.split('@')[0] || 'Owner';
}

export async function signInWithGooglePopup(options?: GooglePopupOptions): Promise<UserCredential> {
    const provider = new GoogleAuthProvider();
    const customParameters: Record<string, string> = { prompt: 'select_account' };
    if (options?.loginHint) {
        customParameters.login_hint = options.loginHint;
    }
    provider.setCustomParameters(customParameters);
    return signInWithPopup(auth, provider);
}

export async function signInWithApplePopup(): Promise<UserCredential> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return signInWithPopup(auth, provider);
}

/**
 * Firebase v12+ — additional user info is read via getAdditionalUserInfo, not on UserCredential.
 * Returns a pending invite's token when the account has no profile yet and was
 * invited (WEB-67): the caller routes to /join instead of a new owner org.
 */
export async function completeOAuthOwnerSignIn(credential: UserCredential): Promise<string | null> {
    return ensureOAuthUserProfile(credential.user, getAdditionalUserInfo(credential));
}

export async function ensureOAuthUserProfile(
    user: User,
    additionalUserInfo?: AdditionalUserInfo | null,
): Promise<string | null> {
    const displayName = resolveOAuthDisplayName(user, additionalUserInfo);

    if (!user.displayName?.trim() && displayName) {
        await updateProfile(user, { displayName });
    }

    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    let joinToken: string | null = null;
    if (!snap.exists()) {
        joinToken = await provisionOwnerUnlessInvited(user, displayName);
    }
    await user.getIdToken(true);
    return joinToken;
}

/**
 * `available` is the set of providers this build can actually offer - see
 * federatedProviders.ts. It is required rather than defaulted on purpose: every
 * branch below tells the user to press a specific button, and gamut-prod renders
 * none of them (WEB-02), so a caller that forgets this argument would produce an
 * instruction pointing at a control that is not on screen. A linked-but-
 * unavailable provider is therefore treated as no usable federated option, which
 * degrades to the plain message rather than to a lie.
 */
export function buildPasswordFailureHintMessage(
    baseMessage: string,
    methods: string[],
    available: readonly FederatedProvider[],
): string {
    const hasGoogle = methods.includes('google.com') && available.includes('google');
    const hasApple = methods.includes('apple.com') && available.includes('apple');
    const hasPassword = methods.includes('password');
    const hasFederated = hasGoogle || hasApple;

    if (hasGoogle && hasApple && !hasPassword) {
        return 'This email is linked to Google and Apple. Use Continue with Google or Continue with Apple to sign in.';
    }
    if (hasGoogle && !hasPassword && !hasApple) {
        return 'This email is linked to Google. Use Continue with Google to sign in.';
    }
    if (hasApple && !hasPassword && !hasGoogle) {
        return 'This email is linked to Apple. Use Continue with Apple to sign in.';
    }
    if (hasFederated && hasPassword) {
        const providers = [hasGoogle ? 'Google' : '', hasApple ? 'Apple' : ''].filter(Boolean).join(' or ');
        return `Invalid email or password. This account can also sign in with ${providers} — use the buttons below.`;
    }
    if (methods.length === 0 && available.length > 0) {
        const names = available.map((p) => (p === 'google' ? 'Google' : 'Apple'));
        const or = names.join(' or ');
        const buttons = names.map((n) => `Continue with ${n}`).join(' or ');
        return `${baseMessage} If you use ${or} for this email, sign in with ${buttons} below.`;
    }
    return baseMessage;
}

export const INVITE_APPLE_EMAIL_MISMATCH_HINT =
    'Sign in with the exact invite email. If Apple offered "Hide My Email", choose "Share My Email" and try again.';

export function formatInviteAcceptanceError(errorCode?: string, errorMessage?: string): string {
    if (errorCode === 'email-mismatch') {
        const base = errorMessage ?? 'This invite was sent to a different email address.';
        return `${base} ${INVITE_APPLE_EMAIL_MISMATCH_HINT}`;
    }
    const msg = errorMessage ?? 'Failed to accept invite.';
    const code = errorCode ? ` (${errorCode})` : '';
    return `${msg}${code}`;
}
